import './FaultsReportWindow.scss'

import {
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    HeaderTitleIcon,
    Modal,
    ModalBody,
    ModalHeader,
    ModalModeType
} from '../../../../../CommonComponents/modals/Modal';
import { CloseSvg, DeleteIcon, FaultInjectIconSvg, PinSvg, PlusSvg, WindowResizeSvg, WindowRightSvg } from '../../../../../CommonComponents/svgs/Svgs';
import { useSmartPlacement } from '../../../../../CommonComponents/modals/hooks/useSmartPlacement';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useForm from '../../../hooks/useForm';
import useAssets from '../../../hooks/useAssets';
import useSelectedGroupSatellite from '../../../hooks/useSelectedGroupSatellite';
import useReportWindow from '../../../hooks/useReportWindow';
import useFaultReportApis from '../../../hooks/useFaultApis';
import FaultReport from './FaultReport';
import useStaticData from '../../../hooks/useStaticData';
import DynamicForm from '../../../../../CommonComponents/dynamic_form/DynamicForm';
import { showToastMessage } from '../../../../../../utils/commonFunctions';
import useFaultApis from '../../../hooks/useFaultApis';
import useNavigation from '../../../hooks/useNavigation';

export const MenuList = [
    {
        name: 'Delete',
        Icon: <DeleteIcon />
    },
]

const FaultsReportWindow = () => {

    const { formData } = useForm()
    const { faultPlans } = useAssets()
    const { missionId } = useNavigation()
    const { getFaultSubsystemByGroupId } = useFaultApis()
    const { allSubsystems, setScenarioBaseData } = useStaticData()
    const { selectedGroupSatellite } = useSelectedGroupSatellite()
    const { windowStore, onReportModalChange } = useReportWindow()
    const { deleteFaultData, postFaultData } = useFaultReportApis()

    const [injectFault, setInjectFault] = useState(false)
    const [loading, setLoading] = useState(false)
    const [openMenu, setOpenMenu] = useState<boolean | string>(false)
    const [selectedFault, setSelectedFault] = useState<string | null>(null)

    const targetId = 'fault__report__button'
    const modalId = 'faultplan__report__modal'

    const windowSize = useMemo(() => windowStore.modals.faultReport.windowSize, [windowStore.modals.faultReport.windowSize])
    const { open, pinned, zIndex, position } = useMemo(() => windowStore.modals.faultReport, [windowStore.modals.faultReport])

    const simulationStartTime = useMemo(() => formData?.schedule?.simulationStartTime, [formData?.schedule?.simulationStartTime])
    const simulationDuration = useMemo(() => formData?.schedule?.simulationDuration, [formData?.schedule?.simulationDuration])
    const simulationEndTime = useMemo(() => simulationStartTime + (simulationDuration * 60), [simulationStartTime, simulationDuration])
    const selectedGroup = useMemo(() => selectedGroupSatellite?.groupId, [selectedGroupSatellite?.groupId])
    const selectedSatellite = useMemo(() => selectedGroupSatellite?.satelliteId, [selectedGroupSatellite?.satelliteId])
    const allFaultsReport = useMemo(() => faultPlans.data, [faultPlans.data])

    const subSystems = useMemo(() => selectedGroup && allSubsystems[selectedGroup] || null, [allSubsystems, selectedGroup])
    const faults = useMemo(() => selectedSatellite && allFaultsReport ? allFaultsReport[selectedSatellite]?.faults || [] : [], [selectedSatellite, allFaultsReport])
    const faultScheduleId = useMemo(() => selectedSatellite && allFaultsReport ? allFaultsReport[selectedSatellite].faultScheduleId : '', [selectedSatellite, allFaultsReport])
    const scheduledFaults = useMemo(() => faults.filter(fault => (fault.startTime < simulationEndTime)), [faults, simulationEndTime])
    const notScheduledFaults = useMemo(() => faults.filter(fault => (fault.startTime > simulationEndTime)), [faults, simulationEndTime])

    const { top, left, bottom, right } = useSmartPlacement(
        targetId,
        modalId,
        "top",
        open,
        pinned,
        {
            top: position.top,
            right: position.right,
            bottom: position.bottom,
            left: position.left
        }
    );

    const getFaultSubsystems = async (groupId: string) => {
        const groupSubsystems = await getFaultSubsystemByGroupId(missionId, groupId)
        setScenarioBaseData((prev) => ({ ...prev, allSubsystems: { ...prev.allSubsystems, [groupId]: groupSubsystems } }))
    }

    useEffect(() => {
        if (!open) setInjectFault(false)
        else if (open && selectedGroup && missionId && !allSubsystems[selectedGroup]) {
            getFaultSubsystems(selectedGroup)
        }
    }, [open, selectedGroup, missionId])

    const onDeleteFault = async (faultId: string) => {
        if (!faultScheduleId) return
        setLoading(true)
        const response = await deleteFaultData(faultScheduleId, faultId)
        showToastMessage(response.status, response.message)
        setLoading(false)
    }

    const onPostFault = async (data: any) => {
        if (!faultScheduleId) return
        setLoading(true)
        const reqBody = {
            system: data.system,
            faultType: data.faultType,
            faultDetails: data.faultDetails,
            executionType: "TimeBase",
            executionRequirement:
            {
                executionTime: data.faultExecutionTime,
                duration: data.duration
            }
        };
        const response = await postFaultData(faultScheduleId, reqBody)
        showToastMessage(response.status, response.message)
        setLoading(false)
    }

    const onSelectFault = (faultId: string) => {
        setSelectedFault((prev => prev === faultId ? null : faultId))
    }

    const getSimulationStartTime = useCallback(() => {
        return !isNaN(new Date(simulationStartTime).getTime()) ? new Date(simulationStartTime * 1000).toISOString().split('.')[0] : ''
    }, [simulationStartTime])

    const getSimulationEndTime = useCallback(() => {
        return !isNaN(new Date(simulationEndTime).getTime()) ? new Date(simulationEndTime * 1000).toISOString().split('.')[0] : ''
    }, [simulationEndTime])

    const onClick = (e: React.MouseEvent, mode: ModalModeType) => {
        onReportModalChange(e, 'root', 'faultReport', mode)
    }

    return (
        <Modal id={modalId} open={open} pinned={pinned} className={`faultplan__report__container`}
            style={{
                minWidth: `${position.width}%`,
                height: `${position.height}%`,
                top: top,
                right: right,
                bottom: bottom,
                left: left,
                zIndex: zIndex
            }}
            onClick={(e) => { onClick(e, 'focus') }}
        >
            <ModalHeader className='faultplan__report__container__header'>
                <HeaderTitleContainer>
                    <HeaderTitleIcon>
                        <FaultInjectIconSvg />
                    </HeaderTitleIcon>
                    <HeaderTitle>
                        Fault Plan
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    <HeaderAction title={pinned ? 'Unpin' : 'Pin to Right'}
                        className={`${pinned && 'active'}`}
                        onClick={(e) => { onClick(e, 'pinned') }}
                    >
                        <WindowRightSvg />
                    </HeaderAction>
                    <HeaderAction title='Resize'
                        onClick={(e) => { onClick(e, 'resize') }}
                    >
                        <WindowResizeSvg size={windowSize} />
                    </HeaderAction>
                    {!injectFault &&
                        <HeaderAction title='Inject Fault'
                            onClick={() => { setInjectFault(true) }}
                        >
                            <PlusSvg />
                        </HeaderAction>
                    }
                    <HeaderAction title='Close'
                        onClick={(e) => { onClick(e, 'close') }}
                    >
                        <CloseSvg />
                    </HeaderAction>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody className='overflow-hidden'>
                <div className={`faultplan__report__container__body 
                    ${injectFault && 'enabled__form__section'} ${loading && 'disabled'}`}
                >
                    <div className='fault__report__list__container'>
                        <div className='fault__report__separator__container'>
                            <div className='fault__report__separator__border' />
                            <span className='fault__report__separator__label'>Simulation Start Time: {getSimulationStartTime()} UTC</span>
                            <div className='fault__report__separator__border' />
                        </div>
                        {scheduledFaults.map(fault => (
                            <FaultReport
                                key={fault.faultId}
                                fault={fault}
                                faultType='SCHEDULED'
                                selectedFault={selectedFault}
                                onSelectFault={onSelectFault}
                                openMenu={openMenu}
                                setOpenMenu={setOpenMenu}
                                onMenuClick={(faultId) => { onDeleteFault(faultId) }}
                            />
                        ))}
                        <div className='fault__report__separator__container'>
                            <div className='fault__report__separator__border' />
                            <span className='fault__report__separator__label'>Simulation End Time: {getSimulationEndTime()} UTC</span>
                            <div className='fault__report__separator__border' />
                        </div>
                        {notScheduledFaults.map(fault => (
                            <FaultReport
                                key={fault.faultId}
                                fault={fault}
                                faultType='NOT_SCHEDULED'
                                selectedFault={selectedFault}
                                onSelectFault={onSelectFault}
                                openMenu={openMenu}
                                setOpenMenu={setOpenMenu}
                                onMenuClick={(faultId) => { onDeleteFault(faultId) }}
                            />
                        ))}
                    </div>
                    <div className={`fautl__form__container`}>
                        <div className='fautl__form__container__header'>
                            <span className='form__header__label'>Inject Fault</span>
                            <button className='form__header__close__button'
                                onClick={() => { setInjectFault(false) }}
                            >
                                <CloseSvg />
                            </button>
                        </div>
                        <div className='fautl__form__container__body'>
                            <DynamicForm
                                supportedSystemFaults={subSystems}
                                postInject={(data) => {
                                    onPostFault(data)
                                }}
                                currentTime={simulationStartTime}
                                editingFault={null}
                                dashboard={'scenarioCreation'}
                                duration={simulationDuration}
                            />
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default FaultsReportWindow